import { put, select, takeEvery } from "redux-saga/effects";

import { BILLING_DOCUMENT, REPORT_JOB } from "constants/actionTypes";
import {
  BillingDocumentReportJobStatus,
  BillingDocumentType,
} from "constants/billingDocuments";
import { ReportJobStatus } from "constants/reportJob";

import toast from "lib/toast";

import { getBillingDocumentById, getBusinesses } from "selectors";
import { ReportJobAction } from "actions/reportJob";
import { takeActionOutcome } from "./lib";
import {
  BulkExportReportJobFailureAction,
  BulkExportReportJobSuccessAction,
} from "actions/reportJob.compile";

export function getBillingDocumentReportJobs(
  billingDocumentIds: string[],
  getBillingDocument: (documentId: string) => BillingDocument | null,
) {
  const reportJobIds: string[] = [];

  const ineligibleBillingDocumentIds: string[] = [];

  billingDocumentIds.forEach(billingDocumentId => {
    const billingDocument: BillingDocument =
      getBillingDocument(billingDocumentId);

    if (!billingDocument) {
      ineligibleBillingDocumentIds.push(billingDocumentId);
      return;
    }

    // Get the most recent report job for the COMMITTED document
    const reportJob: NestedReportJob | null =
      billingDocument.reportJobs.find(
        (billingDocumentReportJob: NestedReportJob) =>
          billingDocumentReportJob.documentStatus ===
          BillingDocumentReportJobStatus.COMMITTED,
      ) || null;

    if (reportJob?.status === ReportJobStatus.SUCCESS) {
      reportJobIds.push(reportJob.id);
    } else {
      ineligibleBillingDocumentIds.push(billingDocumentId);
    }
  });
  return { reportJobIds, ineligibleBillingDocumentIds };
}

type CompileBillingDocumentsAction = {
  type: typeof BILLING_DOCUMENT.COMPILE_DOCUMENTS.ACTION;
  billingDocumentIds: string[];
};

function* onCompileBillingDocuments(action: CompileBillingDocumentsAction) {
  const { billingDocumentIds } = action;

  const state: State = yield select();

  const businessByIdLookup: StateSlice<Business> = getBusinesses(state);

  const { reportJobIds, ineligibleBillingDocumentIds } =
    getBillingDocumentReportJobs(billingDocumentIds, billingDocumentId =>
      getBillingDocumentById(billingDocumentId)(state),
    );

  if (ineligibleBillingDocumentIds.length > 0) {
    const ineligibleBillingDocumentNames = ineligibleBillingDocumentIds
      .map((billingDocumentId: string): BillingDocument | null =>
        getBillingDocumentById(billingDocumentId)(state),
      )
      .filter(Boolean)
      .map((billingDocument: BillingDocument): string => {
        const businessId =
          billingDocument.type === BillingDocumentType.RCTI
            ? billingDocument.recipientBusinessId
            : billingDocument.issuerBusinessId;

        return billingDocument.number
          ? `${billingDocument.type} ${billingDocument.number}`
          : `${billingDocument.type} for ${businessByIdLookup[businessId]?.name}`;
      })
      .join(", ");

    toast.error(
      `The following documents are not ready to be compiled: ${ineligibleBillingDocumentNames}`,
    );

    return;
  }

  yield put(ReportJobAction.bulkExport(reportJobIds));

  yield takeActionOutcome(
    REPORT_JOB.BULK_EXPORT.SUCCESS,
    REPORT_JOB.BULK_EXPORT.FAILURE,
    (compileSucceededAction: BulkExportReportJobSuccessAction) => {
      if (compileSucceededAction.successUrl) {
        window.open(compileSucceededAction.successUrl, "_blank");
      }
    },
    (compileFailedAction: BulkExportReportJobFailureAction) => {
      toast.error(
        `Bulk export responded with code: ${compileFailedAction.status} (${compileFailedAction.statusText})`,
      );
    },
  );
}

export default function* billingDocumentsCompileSaga() {
  yield takeEvery(
    BILLING_DOCUMENT.COMPILE_DOCUMENTS.ACTION,
    onCompileBillingDocuments,
  );
}
